import React, { useCallback, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";

import Buildkite from "../utils/buildkite";
import { getAppUrl } from "../helpers/mappingHelper";

import GeneralSettings from "./GeneralSettings";
import OpsCoreSetup from "./OpsCoreSetup";
import DqiSetup from "./DqiSetup";
import { validate } from "../helpers/validationHelper";
import DemoConfiguration from "../utils/DemoConfiguration";
import { isRunning, isSuccessful } from "../helpers/pipelineStatusHelper";
import auth_config from "../auth_config.json";

const DemoExecution = ({ onLogMessage, onUpdateStatus }) => {
  const [generalSettings, setGeneralSettings] = useState({});
  const [demoState, setDemoState] = useState("none");
  const [validationErrors, setValidationErrors] = useState({});

  const { getAccessTokenSilently } = useAuth0();

  const buildkite = new Buildkite();

  const setStatus = (status, demoConfiguration) => {
    const { app } = demoConfiguration;

    setDemoState(status.state);
    onUpdateStatus(status.state, demoConfiguration.executionTime);

    if (isRunning(status.state)) {
      onLogMessage({
        text: `Still creating ${generalSettings.companyName}`,
        color: "darkgrey",
        app,
      });
    } else {
      if (isSuccessful(status.state)) {
        onLogMessage({
          text: `Company ${
            generalSettings.companyName
          } created! Sign in here: ${getAppUrl(demoConfiguration)}`,
          color: "green",
          app,
        });
      } else {
        onLogMessage({
          text: `Demo company creation failed. Received unexpected status from Buildkite: '${status.state}'.`,
          color: "red",
          app,
        });
      }
    }
  };

  const generalSettingsChanged = useCallback(
    (settings) => {
      setGeneralSettings(settings);
    },
    [setGeneralSettings],
  );

  const executeOpsCoreDemo = async (settings) => {
    await runPipeline("opsCore", settings);
  };

  const executeDqiDemo = async (settings) => {
    await runPipeline("dqi", settings);
  };

  const logSuccess = (create_test_demo_response, demoConfiguration) => {
    const regex =
      /demo_dataset_information_start (.*) demo_dataset_information_end/;
    const instance_information =
      create_test_demo_response.content.match(regex)[1];

    onLogMessage({
      text: `Job and Inventory information for ${demoConfiguration.companyName}:`,
      color: "blue",
      app: demoConfiguration.app,
    });

    onLogMessage({
      text: instance_information,
      color: "blue",
      app: demoConfiguration.app,
    });
  };

  const runPipeline = async (app, settings) => {
    const demoConfiguration = new DemoConfiguration();
    demoConfiguration.executionTime = Date.now();
    Object.assign(demoConfiguration, settings, generalSettings, { app });

    const { errors, valid } = validate(demoConfiguration);

    setValidationErrors(errors);

    if (valid) {
      setDemoState("initiated");
      onUpdateStatus("initiated");

      onLogMessage({
        text: `Creating company ${demoConfiguration.companyName}. This should take ~10 minutes.`,
        color: "blue",
        app,
      });
      try {
        demoConfiguration.token = await getAccessTokenSilently({
          authorizationParams: { audience: auth_config.audience },
        });

        await buildkite.runPipeline(demoConfiguration, setStatus, logSuccess);
      } catch (e) {
        console.log(e);

        setDemoState("Error");
        onLogMessage({
          text: `Demo company creation failed. Received unexpected status from Buildkite: '${e.message}'.`,
          color: "red",
          app,
        });
      }
    }
  };

  const disabled = demoState === "initiated" || isRunning(demoState);

  return (
    <div className="col-md">
      <form
        name="create-demo-company"
        className="card-text"
        onSubmit={(e) => e.preventDefault()}
      >
        <GeneralSettings
          onGeneralSettingsChanged={generalSettingsChanged}
          errors={validationErrors}
        />
        <OpsCoreSetup onExecute={executeOpsCoreDemo} disabled={disabled} />
        <DqiSetup
          onExecute={executeDqiDemo}
          disabled={disabled}
          errors={validationErrors}
        />
      </form>
    </div>
  );
};

DemoExecution.propTypes = {
  onLogMessage: PropTypes.func.isRequired,
  onUpdateStatus: PropTypes.func.isRequired,
};

export default DemoExecution;
